// utils/linkHelpers.js

/**
 * Generates app:// internal link
 * @param {string} type - Link type (mainPage, category, content, book, bible)
 * @param {object} params - Parameters for the link
 * @returns {string} Generated app:// link
 */
export const generateAppLink = (type, params = {}) => {
  switch (type) {
    case 'mainPage':
      // app://videos, app://audios, app://books, app://bible, etc.
      return `app://${params.page}`;

    case 'audioCategory':
      return `app://category/audio/${params.categoryId}`;

    case 'videoCategory':
      return `app://category/video/${params.categoryId}`;

    // A series is a video category (categoryType=series); it opens the same in-app
    // screen, which shows its seasons → episodes (or episodes directly if no seasons).
    case 'series':
      return `app://category/video/${params.categoryId}`;

    case 'bookCategory':
      return `app://category/book/${params.categoryId}`;

    // Misc = the More (بیشتر) section.
    case 'miscCategory':
      return `app://category/misc/${params.categoryId}`;

    case 'audioContent':
      return `app://content/audio/${params.contentId}`;

    case 'videoContent':
      return `app://content/video/${params.videoId}`;

    case 'miscContent':
      return `app://content/misc/${params.contentId}`;

    case 'book':
      return `app://book/${params.bookId}`;

    case 'bible':
      return `app://bible/${params.languageId}/${params.bookCode}/${params.chapter}`;

    default:
      return '';
  }
};

/**
 * Parses app:// link to extract type and parameters
 * @param {string} link - The app:// link to parse
 * @returns {object} { type, params, isValid }
 */
export const parseAppLink = (link) => {
  if (!link || !link.startsWith('app://')) {
    return { type: null, params: {}, isValid: false };
  }

  // Main pages
  const mainPageMatch = link.match(/^app:\/\/(videos|audios|books|bible|home|search|worship|more|aichat)$/);
  if (mainPageMatch) {
    return {
      type: 'mainPage',
      params: { page: mainPageMatch[1] },
      isValid: true
    };
  }

  // Category links
  const categoryMatch = link.match(/^app:\/\/category\/(audio|video|book|misc)\/([a-f0-9]{24})$/);
  if (categoryMatch) {
    const [, contentType, categoryId] = categoryMatch;
    return {
      type: `${contentType}Category`,
      params: { categoryId },
      isValid: true
    };
  }

  // Content links
  const contentMatch = link.match(/^app:\/\/content\/(audio|video|misc)\/([a-f0-9]{24})$/);
  if (contentMatch) {
    const [, contentType, contentId] = contentMatch;
    return {
      type: `${contentType}Content`,
      params: contentType === 'video' ? { videoId: contentId } : { contentId },
      isValid: true
    };
  }

  // Book link
  const bookMatch = link.match(/^app:\/\/book\/([a-f0-9]{24})$/);
  if (bookMatch) {
    return {
      type: 'book',
      params: { bookId: bookMatch[1] },
      isValid: true
    };
  }

  // Bible link
  const bibleMatch = link.match(/^app:\/\/bible\/([a-f0-9]{24})\/([A-Z]{3})\/(\d+)$/);
  if (bibleMatch) {
    return {
      type: 'bible',
      params: {
        languageId: bibleMatch[1],
        bookCode: bibleMatch[2],
        chapter: bibleMatch[3]
      },
      isValid: true
    };
  }

  return { type: null, params: {}, isValid: false };
};

/**
 * Gets human-readable description of a link
 * @param {string} link - The link to describe
 * @param {object} metadata - Optional metadata (titles, names, etc.)
 * @returns {string} Human-readable description
 */
export const getLinkDescription = (link, metadata = {}) => {
  if (!link) return '';

  // External URL
  if (link.startsWith('http://') || link.startsWith('https://')) {
    return `External: ${link}`;
  }

  const parsed = parseAppLink(link);
  if (!parsed.isValid) return 'Invalid link';

  switch (parsed.type) {
    case 'mainPage':
      const pageNames = {
        videos: 'Videos Main Page',
        // app://audios opens the Home tab, not an audio list — see deep_link_handler.dart.
        audios: "Home Screen (legacy 'Audios')",
        books: 'Books Main Page',
        bible: 'Bible Main Page',
        home: 'Home Screen',
        search: 'Search',
        worship: 'Worship',
        more: 'More (بیشتر)',
        aichat: 'AI Chat'
      };
      return pageNames[parsed.params.page] || parsed.params.page;

    case 'audioCategory':
      return metadata.categoryTitle || `Audio Category`;

    case 'videoCategory':
      return metadata.categoryTitle || `Video Category`;

    case 'bookCategory':
      return metadata.categoryTitle || `Book Category`;

    case 'miscCategory':
      return metadata.categoryTitle || `More Category`;

    case 'audioContent':
      return metadata.contentTitle || `Audio Content`;

    case 'videoContent':
      return metadata.videoTitle || `Video Content`;

    case 'miscContent':
      return metadata.contentTitle || `More Content`;

    case 'book':
      return metadata.bookTitle || `Book`;

    case 'bible':
      if (metadata.bibleDescription) {
        return metadata.bibleDescription;
      }
      const chapterText = parsed.params.chapter === '0' || parsed.params.chapter === 0
        ? 'Introduction'
        : `Chapter ${parsed.params.chapter}`;
      return `Bible: ${parsed.params.bookCode} ${chapterText}`;

    default:
      return 'Unknown link type';
  }
};
